import { voiceForAnalytics } from "~/features/meditations/types";
import { usePractitionerVoicePromo } from "~/features/meditations/hooks/use-practitioner-voice-promo";
import { PractitionerVoicePromoDialog } from "~/features/meditations/components/practitioner-voice-promo-dialog";
import React, { useEffect, useMemo, useState, useCallback } from "react";

import { useSearchParams } from "@remix-run/react";
import { Headphones, Play } from "lucide-react";

import { AiDisclosureBanner } from "~/components/ai-disclosure-banner";
import { Button } from "~/components/ui/button";
import { H3 } from "~/components/ui/typography";
import { useUserDetails } from "~/hooks/queries/use-user-details";
import { trackEvent } from "~/lib/analytics";
import { cn } from "~/lib/utils";

import type { SelectableVoice } from "../types";
import type { MeditationSetupSelection } from "./meditation-setup";
import { useMeditationVoiceOptions } from "../hooks/use-meditation-voice-options";
import { useMeditationsBySession } from "../hooks/use-meditations-by-session";
// AIDEV-NOTE: EVO-2476 — the player prints a total only once the HLS playlist finalizes, so a
// fresh generation showed elapsed time alone while a cached replay showed "0:07 / 19:45".
// Catalog meditations carry an authored length, so hand it down and the readout is the same
// either way.
import {
    estimateMeditationMinutes,
    isSoloMeditationEligible,
} from "../utils/meditation-catalog";
import { resolveCachedPartMeditationId } from "../utils/resolve-part-meditation-playback";
import { MeditationCacheBadge } from "./meditation-cache-badge";
import { MeditationCard } from "./meditation-card";
import { MeditationSetupOverlay } from "./meditation-setup";
import { StreamingMeditationPlayer } from "./streaming-meditation-player";

// AIDEV-NOTE: Part Check-In card for the session-notes part detail sidebar. Renders the
// SAME MeditationCard + shared voice dropdown as the standalone part detail page
// (_protected.parts.$partId._index.tsx) so the two surfaces read identically — same
// title, description, control row and Play CTA. Opens the fullscreen streaming player.
/**
 * The strip's surface — white, lit by a faint forest glow rising out of the lower-LEFT
 * corner, balancing the play control on the right.
 *
 * AIDEV-NOTE: Exported because the wrapper in parts-list.tsx paints the same surface, so
 * the strip and its frame hover and rest as one object. One definition, both surfaces.
 */
export const PART_MEDITATION_STRIP_SURFACE =
    "bg-white bg-[radial-gradient(430px_115px_at_0%_122%,rgba(45,95,74,0.094),rgba(45,95,74,0.033)_46%,rgba(45,95,74,0)_76%)]";

interface PartMeditationButtonProps {
    /**
     * The session this part interaction came from, when there is one.
     * AIDEV-NOTE: Optional because chat- and note-based parts (add-part) have no session,
     * and part meditation generation supports that case — requiring it here removed their
     * entry point entirely. Only the public cached-replay lookup actually needs it.
     */
    sessionId?: string;
    partInteractionId: string;
    partName: string;
    isPublicSession?: boolean;
    // AIDEV-NOTE: Part's generated image (signed URL); shown as the fullscreen player's left-pane icon.
    partImageUrl?: string;
    /**
     * "strip" renders a compact CTA bar for the foot of a part card instead of the full
     * MeditationCard.
     *
     * AIDEV-NOTE: Same streaming path, different surface — the card variant carries a
     * disclosure banner, voice picker and cache badge, which is right for a dedicated
     * meditation panel and far too heavy sitting under every part on the session page.
     * Meditations lost their session tab, so this strip is now the way in: you reach a
     * meditation through the part you want to sit with.
     */
    variant?: "card" | "strip" | "cardAction";
    /**
     * The interaction's stored part type. Unattached burdens and "Self" entries are not parts
     * and get no solo meditation entry point (see isSoloMeditationEligible). Omit only where
     * the caller has already excluded them.
     */
    partType?: string | null;
    /**
     * The SUBJECT's meditation preference, when the viewer is not the subject.
     *
     * AIDEV-NOTE: `useUserDetails().showMeditations` is the VIEWER's gate, and a
     * practitioner's own gate is always open (EVO-1969) — so on a client's part page or
     * session notes it answered the wrong question and showed meditation entry points the
     * practitioner had disabled for that client. Loaders that render someone else's parts
     * resolve the owner with getShowMeditationsFlag and pass it here. Undefined keeps the
     * viewer's own gate, which is correct on your own pages.
     */
    meditationsEnabledForSubject?: boolean;
}

export const PartMeditationButton: React.FC<PartMeditationButtonProps> = ({
    sessionId,
    partInteractionId,
    partName,
    isPublicSession,
    partImageUrl,
    variant = "card",
    partType,
    meditationsEnabledForSubject,
}) => {
    const [isStreaming, setIsStreaming] = useState(false);
    const [isSetupOpen, setIsSetupOpen] = useState(false);
    const [setupSelection, setSetupSelection] =
        useState<MeditationSetupSelection | null>(null);
    const { data: userDetails } = useUserDetails();
    const [searchParams] = useSearchParams();
    // AIDEV-NOTE: Part check-ins use the shared voice picker. Default = the
    // account's custom (cloned) voice when available, matching the session
    // meditation picker; otherwise Brad. Same voice-keyed cache flow applies.
    const {
        options: voiceOptions,
        defaultVoice,
        customVoiceKey,
        requestableVoices,
        customVoiceAvailable,
    } = useMeditationVoiceOptions();
    const [selectedVoice, setSelectedVoice] = useState<SelectableVoice>("Brad");

    // AIDEV-NOTE: Sync from URL / custom-voice availability only (mount,
    // navigation, or when the custom-voice probe resolves), not on user pick.
    useEffect(() => {
        const requested = searchParams.get("voice");
        const matched = voiceOptions.find((v) => v.value === requested)?.value;
        setSelectedVoice(matched ?? defaultVoice);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, customVoiceKey]);

    const voicePromo = usePractitionerVoicePromo({
        requestableVoices,
        hasReadyVoice: customVoiceAvailable,
    });

    // AIDEV-NOTE: Token-shared views are READ-ONLY for meditations: playback of an
    // already-cached artifact is allowed, generation is not (the endpoints 404 a cache miss
    // — see features/meditations/server/public-access.server.ts). So on a share link we list
    // the session's meditations (the status endpoint binds that list to the token's own
    // session), and only render the card when a COMPLETED one exists for this interaction.
    const sessionToken = searchParams.get("sessionToken") ?? "";
    const { data: sessionMeditations } = useMeditationsBySession(
        sessionId ?? "",
        {
            enabled: !!isPublicSession && !!sessionId,
            sessionToken,
        },
    );
    const cachedMeditationId = useMemo(
        () =>
            isPublicSession
                ? resolveCachedPartMeditationId({
                      meditations: sessionMeditations ?? [],
                      partInteractionId,
                  })
                : undefined,
        [isPublicSession, sessionMeditations, partInteractionId],
    );

    const startPublicPlayback = useCallback(() => {
        setIsStreaming(true);
        trackEvent("streaming meditation started", {
            type: "part",
            sessionId,
            partId: partInteractionId,
            voice: voiceForAnalytics(selectedVoice),
        });
    }, [sessionId, partInteractionId, selectedVoice]);

    const openSetup = useCallback(() => {
        setIsSetupOpen(true);
    }, []);

    // AIDEV-NOTE: One-time promo gate. Returns true when there's nothing to show, so the
    // overwhelmingly common path is a straight passthrough to setup.
    const handlePlay = useCallback(() => {
        // AIDEV-NOTE: Token viewers retain direct cached replay; setup would expose generation controls.
        const next = isPublicSession ? startPublicPlayback : openSetup;
        if (isPublicSession || voicePromo.interceptPlay(next)) next();
    }, [isPublicSession, openSetup, startPublicPlayback, voicePromo]);

    const handleSetupStart = useCallback(
        (selection: MeditationSetupSelection) => {
            if (selection.kind !== "part") return;
            setSelectedVoice(selection.voice);
            setSetupSelection(selection);
            setIsSetupOpen(false);
            setIsStreaming(true);
            trackEvent("streaming meditation started", {
                type:
                    selection.option.backing === "resource"
                        ? "exercise"
                        : selection.option.backing === "session"
                          ? "session"
                          : "part",
                sessionId,
                partId: partInteractionId,
                voice: voiceForAnalytics(selection.voice),
                meditationKind: selection.option.id,
                pauseMultiplier: selection.pauseMultiplier,
                opening: selection.opening,
            });
        },
        [partInteractionId, sessionId],
    );

    const handleClose = useCallback(() => {
        setIsStreaming(false);
        setSetupSelection(null);
    }, []);

    // AIDEV-NOTE: Two different gates, deliberately. A token viewer has no account and so no
    // `showMeditations` flag of their own — for them the only question is "does a cached
    // meditation already exist for this part?". Everyone else keeps the EVO-1428 flag gate.
    if (isPublicSession) {
        if (!cachedMeditationId) {
            return null;
        }
    } else if (
        meditationsEnabledForSubject !== undefined
            ? !meditationsEnabledForSubject
            : !userDetails?.showMeditations
    ) {
        return null;
    } else if (!isSoloMeditationEligible(partType)) {
        // AIDEV-NOTE: Nothing to connect with — unattached burdens and "Self" rows are not
        // parts (IFS consultant review, 2026-09-09). Token viewers are unaffected: they only
        // ever replay audio that already exists.
        return null;
    }

    // AIDEV-NOTE: Both things handlePlay can put on screen are defined HERE, once, above
    // the variant branch — the player it opens and the voice promo that can gate it.
    //
    // They used to be written out inside each variant's return, and the strip shipped
    // without the promo: handlePlay calls voicePromo.interceptPlay, which on a promo-eligible
    // account marks the promo seen, stores the pending play and returns false, expecting a
    // dialog to finish the job. With none mounted the press did nothing at all, and stranded
    // the module-level promoOpenOwner lock (only resolve() clears it) so every OTHER play
    // button then skipped the promo. A shared handler whose UI each caller has to remember
    // to mount is a trap. Any new variant now gets both by referencing these.
    let player: React.ReactNode = null;
    if (isStreaming && isPublicSession) {
        player = (
            // AIDEV-NOTE: `meditationId` keeps token viewers on cached replay, never generation.
            <StreamingMeditationPlayer
                type="part"
                voice={selectedVoice}
                sessionId={sessionId}
                partInteractionId={partInteractionId}
                meditationId={cachedMeditationId}
                partName={partName}
                partImageUrl={partImageUrl}
                // AIDEV-NOTE: Every variant but the legacy inline card plays fullscreen.
                // Listing only "strip" silently downgraded cardAction's public cached
                // replay to an inline player.
                fullscreen={variant !== "card"}
                autoStart
                onClose={handleClose}
            />
        );
    } else if (isStreaming && setupSelection?.kind === "part") {
        const { option, voice, pauseMultiplier, opening } = setupSelection;
        if (option.backing === "generated") {
            player = (
                <StreamingMeditationPlayer
                    type="part"
                    voice={voice}
                    sessionId={sessionId}
                    partInteractionId={partInteractionId}
                    partName={partName}
                    partImageUrl={partImageUrl}
                    pauseMultiplier={pauseMultiplier}
                    estimatedTotalSeconds={
                        estimateMeditationMinutes(option, pauseMultiplier) * 60
                    }
                    opening={opening}
                    fullscreen
                    autoStart
                    onClose={handleClose}
                />
            );
        } else if (option.backing === "session") {
            // AIDEV-NOTE: The full-session meditation is about the SESSION, not this part —
            // no partName / partImageUrl, so the player titles it as the session meditation
            // rather than presenting it as another check-in with this part. The session id
            // comes from the option (resolved server-side), not this component's optional prop.
            player = (
                <StreamingMeditationPlayer
                    type="session"
                    voice={voice}
                    sessionId={option.sessionId}
                    title={option.title}
                    meditationTypeLabel={option.title}
                    pauseMultiplier={pauseMultiplier}
                    estimatedTotalSeconds={
                        estimateMeditationMinutes(option, pauseMultiplier) * 60
                    }
                    fullscreen
                    autoStart
                    onClose={handleClose}
                />
            );
        } else {
            // AIDEV-NOTE: Exercise URLs mirror ExercisePlayer; slugs resolve server-side to the current UUID.
            const url = new URL(
                "/api/meditation/stream",
                window.location.origin,
            );
            url.searchParams.set("experienceId", option.experienceId);
            url.searchParams.set("voice", voice);
            url.searchParams.set("selectedPartIds", setupSelection.part.partId);
            url.searchParams.set("pauseMultiplier", String(pauseMultiplier));
            player = (
                <StreamingMeditationPlayer
                    key={url.toString()}
                    type="part"
                    voice={voice}
                    title={option.titleForPlayer}
                    captureTitle={option.resourceTitle}
                    streamUrl={url.toString()}
                    experienceId={option.experienceId}
                    selectedPartIds={[setupSelection.part.partId]}
                    captureKind={option.config.captureKind}
                    captureFields={option.config.captureFields}
                    capturePartId={setupSelection.part.partId}
                    capturePartType={setupSelection.part.partType}
                    partName={partName}
                    partImageUrl={partImageUrl}
                    meditationTypeLabel={option.config.subtitle}
                    pauseMultiplier={pauseMultiplier}
                    estimatedTotalSeconds={
                        estimateMeditationMinutes(option, pauseMultiplier) * 60
                    }
                    // handleSetupStart already fired "streaming meditation started".
                    startedEventTracked
                    fullscreen
                    autoStart
                    onClose={handleClose}
                />
            );
        }
    }

    const setup =
        isSetupOpen && !isPublicSession ? (
            <MeditationSetupOverlay
                mode={{ kind: "part", partInteractionId, partName }}
                partImageUrl={partImageUrl}
                voiceOptions={voiceOptions}
                initialVoice={selectedVoice}
                defaultVoice={defaultVoice}
                onClose={() => setIsSetupOpen(false)}
                onStart={handleSetupStart}
            />
        ) : null;

    // AIDEV-NOTE: The voice-clone promo is an account/upsell flow — never shown to a
    // share-link viewer, who cannot own or request a voice.
    const promo = !isPublicSession ? (
        <PractitionerVoicePromoDialog
            open={voicePromo.isOpen}
            target={voicePromo.target}
            onResolved={voicePromo.resolve}
        />
    ) : null;

    // AIDEV-NOTE: `cardAction` is the CTA alone — no card, no banner. The
    // part detail page draws its own card (matching the journal card beside it) and only
    // needs the button, but it still needs everything this component OWNS: the setup
    // overlay, the player and the voice-clone promo gate. Rendering a bare <Button> there
    // instead would strand handlePlay's promo interception, which is the trap the strip
    // variant's note describes.
    if (variant === "cardAction") {
        return (
            <>
                <Button
                    variant="solidForest"
                    onClick={handlePlay}
                    disabled={isStreaming}
                    data-testid="part-meditation-card-action"
                    // AIDEV-NOTE: Anchor for the post-enable DPMP tour's voice step
                    // (packages/config/dpmp-tour.tsx) — the voice is chosen in the setup
                    // overlay this button opens, so the tour points at the button.
                    data-tour-id="part-meditation-connect"
                    analyticsId="Connect With This Part (Part Detail)"
                    className="shrink-0 gap-2"
                >
                    <Play className="size-4 fill-current" />
                    {isStreaming ? "Starting…" : "Connect with Part"}
                </Button>
                {setup}
                {player}
                {promo}
            </>
        );
    }

    if (variant === "strip") {
        return (
            <>
                {/* AIDEV-NOTE: A barely-there forest wash rather than a solid green bar —
                    it belongs to the same family as the shadow the part card casts down
                    onto it, so the seam reads as one surface meeting another instead of
                    two colours butting together. The slightly darker gradient enters from
                    the right and fades out before mid-row, which is what carries the eye
                    to the play control. The label and play triangle sit together at the
                    right (what pressing it does); the left edge is deliberately empty —
                    a headphones badge there read as a second, competing control.

                    No inset shadow here: the seam is cast DOWN onto this row by the
                    part card sitting above it. */}
                <button
                    type="button"
                    onClick={handlePlay}
                    disabled={isStreaming}
                    data-testid="part-meditation-strip"
                    className={cn(
                        "flex w-full items-center gap-2.5 px-[18px] py-2.5 transition-colors disabled:opacity-70",
                        PART_MEDITATION_STRIP_SURFACE,
                        // The wrapper hovers as one with this row, so the tucked region
                        // behind the card's corners never lags a colour behind.
                        "group-hover/strip:bg-brand-forest-50",
                    )}
                >
                    <span className="ml-auto text-right text-[15px] font-semibold text-brand-forest-800">
                        {isStreaming ? "Starting…" : "Connect with this Part"}
                    </span>
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-forest-200 text-brand-forest-800">
                        <Play className="size-[13px] translate-x-[1px] fill-current" />
                    </span>
                </button>
                {setup}
                {player}
                {promo}
            </>
        );
    }

    return (
        <>
            {/* AIDEV-NOTE: Spacing is deliberately uneven. The disclosure is a
                one-time notice — once dismissed the controls become the top of the
                section — so it gets a wider gap beneath it than the controls get
                between themselves, and the section reads correctly in both states
                rather than leaving a hole where the banner used to be. */}
            {/* AIDEV-NOTE: Plain `sm:` breakpoints, NOT a container query. Tailwind's
                @container sets container-type: inline-size, which stops the element
                sizing from its contents — in this panel that collapsed the whole
                section to a ~150px column and squeezed the banner, tiles and CTA.
                w-full did not rescue it. Not worth the fight for one grid change. */}
            <div className="flex w-full flex-col items-stretch gap-4">
                {/* AIDEV-NOTE: This card is a meditation entry point in its own right, so it
                    carries the disclosure too (QA, Jul 2026). Shares the "meditations"
                    dismissal key with the list and session surfaces, so dismissing on any one
                    of them dismisses all three. The standalone part page has no equivalent
                    banner — flagged to Adam, Aug 2026; not resolved here either way. */}
                <AiDisclosureBanner surface="meditations" className="w-full" />

                {/* AIDEV-NOTE: No inline voice dropdown (removed 2026-09-10). Play opens the
                    setup overlay, whose Voice row (with previews) is the one picker for every
                    surface; a second control here meant choosing twice. */}
                <MeditationCard
                    title={
                        // AIDEV-NOTE: Matches the standalone part page card exactly —
                        // H3 + size-5 headphones. Keep the two in step.
                        <H3 className="mb-0 flex items-center gap-2">
                            <Headphones className="size-5" />
                            Part Check-In
                        </H3>
                    }
                    description="Listen to a guided meditation designed for checking-in with this part."
                    crafted
                    hasMeditation
                    playVariant="default"
                    isPlayingActive={isStreaming}
                    isGenerating={isStreaming}
                    onPlay={handlePlay}
                    analyticsId="Play Part Meditation (Sidebar)"
                />

                {/* AIDEV-NOTE: Internal-only cache badge — renders null without the
                    Internal Features flag, so it sits outside the card rather than
                    forcing a slot into the shared component. */}
                {!isPublicSession && (
                    <MeditationCacheBadge
                        type="part"
                        voice={selectedVoice}
                        sessionId={sessionId}
                        partInteractionId={partInteractionId}
                    />
                )}
            </div>

            {setup}
            {player}
            {promo}
        </>
    );
};
